import React from 'react';
import { withTranslation } from 'react-i18next';
import { compose, mapProps } from 'recompose';
import { graphql } from 'react-apollo';
import { FormHelperText, MenuItem } from '@material-ui/core';

import Button from 'components/common/Buttons/Button';
import InputField from 'components/common/InputField/InputField';
import { createPasswordValidationFn, createTextValidationFn } from 'utils/validations/validations';
import { withRouter } from 'react-router-dom';
import ThreeppModal from 'components/common/Modal/Modal';
import { getCurrentUser, updateUserProfile, setCurrentUser } from 'graphql/operations';
import { getCurrentUser as getCurrentUserSelector } from 'selectors/user/selector';
import { Select } from 'components/common/Select/Select';

import { showSuccessMessage, showErrorMessage } from 'components/common/Alert/Alert';
import { isEmptyString } from 'utils/functions';
import { Languages } from 'utils/constants';
import i18n from '../../../i18n';

import './UserProfile.scss';

const INITIAL_STATE = {
  firstName: '',
  firstNameError: '',
  lastName: '',
  lastNameError: '',
  language: '',
  updateError: false
};

function getValidationFunction() {
  return createTextValidationFn();
}

export class UserProfile extends React.Component {
  static defaultProps = {
    toggle: k => k
  };

  constructor(props) {
    super(props);
    this.state = { ...INITIAL_STATE };
    this.validatePassword = createPasswordValidationFn();
  }

  // DOM Generator
  createLanguageOptions = () => {
    const dropdownOptions = [];
    const languages = [
      { english: 'English' },
      { spanish: 'Spanish' },
      { german: 'German' },
      { french: 'French' },
      { italian: 'Italian' },
      { japanese: 'Japanese' },
      { portuguese: 'Portuguese' },
      { chinese: 'Chinese' },
      { korean: 'Korean' }
    ];
    languages.forEach(language =>
      Object.entries(language).forEach(([key, value]) =>
        dropdownOptions.push(
          <MenuItem value={key} key={key}>
            {value}
          </MenuItem>
        )
      )
    );
    return dropdownOptions;
  };

  onOpened = () => {
    const { currentUser } = this.props;
    this.setState({
      ...INITIAL_STATE,
      firstName: currentUser.firstName,
      lastName: currentUser.lastName,
      language: currentUser.language ? currentUser.language : ''
    });
  };

  close = () => {
    const { toggle } = this.props;
    toggle();
  };

  onSave = async () => {
    const { currentUser, updateUserProfile: propsUpdateUserProfile, setCurrentUser: propsSetCurrentUser, t } = this.props;
    const { firstName, lastName, language } = this.state;
    let error = false;
    if (this.isUserDetailsUpdated()) {
      try {
        const {
          data: { updateUserProfile: updatedUser }
        } = await propsUpdateUserProfile({
          variables: {
            email: currentUser.email,
            firstName,
            lastName,
            country: 'US',
            position: currentUser.position,
            language
          }
        });

        await propsSetCurrentUser({
          variables: {
            email: updatedUser.email,
            firstName: updatedUser.firstName,
            lastName: updatedUser.lastName,
            position: updatedUser.position,
            language: updatedUser.language
          }
        });
        i18n.changeLanguage(Languages[language?.toUpperCase()]);
        document.documentElement.setAttribute('lang', Languages[language?.toUpperCase()]);
        showSuccessMessage(t('userProfile.userDetailsUpdateSuccess'));
      } catch {
        showErrorMessage(t('userProfile.userDetailsUpdateError'));
        error = true;
      }
    }

    if (!error) {
      this.close();
    } else {
      this.setState({ updateError: true });
    }
  };

  setFirstName = value => {
    this.setState({
      firstName: value.trim(),
      firstNameError: '',
      updateError: false
    });
  };

  setLastName = value => {
    this.setState({
      lastName: value.trim(),
      lastNameError: '',
      updateError: false
    });
  };

  setLanguage = event => {
    this.setState({
      language: event.target.value,
      updateError: false
    });
  };

  changePasswordLink = () => {
    this.close();
    let issuerUrl;
    Object.entries(localStorage).some(([key, val]) => {
      const keyPieces = key.split('.');
      if (keyPieces[0] === 'CognitoIdentityServiceProvider' && keyPieces[keyPieces.length - 1] === 'idToken') {
        issuerUrl = JSON.parse(atob(val.split('.')[1])).okta_issuer_url;
        return true;
      }
      return false;
    });
    window.open(`${issuerUrl}/enduser/settings`, '_blank');
  };

  handleFirstNameError = () => {
    const { t } = this.props;
    this.setState({
      firstNameError: t('userProfile.fieldIsRequired'),
      firstName: ''
    });
  };

  handleLastNameError = () => {
    const { t } = this.props;
    this.setState({
      lastNameError: t('userProfile.fieldIsRequired'),
      lastName: ''
    });
  };

  isUserDetailsUpdated() {
    const { currentUser } = this.props;
    const { firstName, lastName, language } = this.state;
    const isUserNameUpdated = !(firstName === currentUser.firstName && lastName === currentUser.lastName);
    const isLanguageUpdated = !!language && language !== currentUser.language;
    return isUserNameUpdated || isLanguageUpdated;
  }

  isSaveDisabled() {
    const { firstName, lastName, updateError } = this.state;
    return !this.isUserDetailsUpdated() || isEmptyString(firstName) || isEmptyString(lastName) || updateError;
  }

  render() {
    const { t, isOpen, toggle } = this.props;
    const { firstName, firstNameError, lastName, lastNameError, language = '' } = this.state;

    const inputValidation = getValidationFunction();

    return (
      <>
        <ThreeppModal
          onOpened={this.onOpened}
          isOpen={isOpen}
          toggle={toggle}
          classes="user-profile-modal"
          header={t('userProfile.header')}
          size="sm"
        >
          <InputField
            id="userProfile-firstName"
            label={t('userProfile.firstName')}
            fullWidth
            disabled={false}
            value={firstName}
            inputValidation={inputValidation}
            onChange={this.setFirstName}
            error={firstNameError}
            onValidationError={this.handleFirstNameError}
          />

          <InputField
            id="userProfile-lastName"
            label={t('userProfile.lastName')}
            fullWidth
            disabled={false}
            value={lastName}
            inputValidation={inputValidation}
            onChange={this.setLastName}
            error={lastNameError}
            onValidationError={this.handleLastNameError}
          />

          <Select
            id="userProfile-langSelection"
            variant="filled"
            label={t('userProfile.language')}
            value={language}
            onChange={this.setLanguage}
            defaultValue=""
          >
            {this.createLanguageOptions()}
          </Select>
          <FormHelperText className="field-info">{t('userProfile.languageInfo')}</FormHelperText>

          {/* TODO: Fix this... */}
          {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events  */}

          <div className="action-row">
            <div
              id="userProfile-changePassword"
              className="change-password"
              onClick={this.changePasswordLink}
              onKeyPress={this.changePasswordLink}
              tabIndex={0}
              role="link"
            >
              {t('userProfile.changePassword')}
            </div>
            {/* TODO: Fix this... */}
            {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events  */}
            <div className="btn-group">
              <Button testId="userProfile-cancel" className="btn-cancel" buttonStyle="destructive" onClick={this.close}>
                {t('common.cancel')}
              </Button>

              <Button testId="userProfile-save" className="btn-save" buttonStyle="primary" onClick={this.onSave} disabled={this.isSaveDisabled()}>
                {t('common.save')}
              </Button>
            </div>
          </div>
        </ThreeppModal>
      </>
    );
  }
}

export const UserProfileHOC = compose(
  withTranslation(),
  withRouter,
  graphql(updateUserProfile, { name: 'updateUserProfile' }),
  graphql(setCurrentUser, { name: 'setCurrentUser' }),
  graphql(getCurrentUser, { name: 'getCurrentUser' }),
  mapProps(props => ({
    t: props.t,
    history: props.history,
    toggle: props.toggle,
    isOpen: props.isOpen,
    currentUser: getCurrentUserSelector(props),
    updateUserProfile: props.updateUserProfile,
    setCurrentUser: props.setCurrentUser
  }))
);

export default UserProfileHOC(UserProfile);
